import {
  useGetList,
  useNotify,
  usePermissions,
  useTranslate,
  useUpdate,
} from "ra-core";
import { ArrowDown, ArrowUp } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { AutomationRule } from "../../../../supabase/functions/_shared/automation/types.ts";
import {
  emptyRule,
  nextPosition,
  reorderPositions,
  ruleStats,
  safeCode,
  type EditableRule,
} from "./ruleModel";
import { RuleEditor } from "./RuleEditor";
import { RunsHistory } from "./RunsHistory";

const fmt = new Intl.DateTimeFormat("fr-CA", {
  timeZone: "America/Toronto",
  dateStyle: "short",
  timeStyle: "short",
});
const defaultNow = () => new Date();
type Run = { rule_id: number; status: string; created_at: string };

/**
 * /parametres/automatisations (spec 05 §7). Both roles: admins edit, reorder, toggle and see the
 * run history; reps (and anyone whose role is not yet known) get a read-only view of the rules.
 * The database enforces the same split (RLS on automation_rules / automation_runs).
 */
export const AutomationRulesPage = ({
  now = defaultNow,
}: {
  now?: () => Date;
}) => {
  const t = useTranslate();
  const notify = useNotify();
  const { permissions } = usePermissions();
  const admin = permissions === "admin";
  const { data: rules = [], refetch } = useGetList<AutomationRule>(
    "automation_rules",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "position", order: "ASC" },
    },
  );
  const { data: runs = [] } = useGetList<Run & { id: number }>(
    "automation_runs",
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "created_at", order: "DESC" },
    },
    { enabled: admin, retry: false },
  );
  const stats = useMemo(() => ruleStats(runs, now()), [runs, now]);
  const [update] = useUpdate();
  const [editing, setEditing] = useState<EditableRule | null>(null);

  const [moving, setMoving] = useState(false);
  // Renumbers the whole list (10, 20, 30…). A partial failure leaves a mixed order, so the list
  // is always refetched to show what the database actually holds.
  const move = async (from: number, to: number) => {
    setMoving(true);
    try {
      for (const p of reorderPositions(rules, from, to)) {
        await update(
          "automation_rules",
          {
            id: p.id,
            data: { position: p.position },
            previousData: rules.find((r) => r.id === p.id),
          },
          { mutationMode: "pessimistic", returnPromise: true },
        );
      }
    } catch {
      notify("gavion.automation.errors.save_failed", { type: "error" });
    } finally {
      setMoving(false);
      await refetch();
    }
  };

  if (editing) {
    return (
      <RuleEditor
        rule={editing}
        readOnly={!admin}
        now={now}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          void refetch();
        }}
      />
    );
  }

  const lastRun = (id: number) => {
    const s = stats[id];
    if (!s?.lastRunAt) return "—";
    const status = s.lastStatus
      ? t(`gavion.automation.statuses.${s.lastStatus}`, {
          _: safeCode(s.lastStatus) ?? "",
        })
      : "";
    return `${fmt.format(new Date(s.lastRunAt))} · ${status}`;
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-extrabold">
          {t("gavion.automation.title")}
        </h1>
        {admin && (
          <Button
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            onClick={() =>
              setEditing({
                ...emptyRule("call_outcome"),
                position: nextPosition(rules),
              })
            }
          >
            {t("gavion.automation.new_rule")}
          </Button>
        )}
      </div>
      {!admin && (
        <p className="text-sm text-muted-foreground">
          {t("gavion.automation.read_only")}
        </p>
      )}
      <Tabs defaultValue="rules">
        <TabsList>
          <TabsTrigger value="rules">
            {t("gavion.automation.tabs.rules")}
          </TabsTrigger>
          {admin && (
            <TabsTrigger value="history">
              {t("gavion.automation.tabs.history")}
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="rules">
          {rules.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("gavion.automation.none")}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("gavion.automation.columns.name")}</TableHead>
                    <TableHead>
                      {t("gavion.automation.columns.trigger")}
                    </TableHead>
                    {admin && (
                      <>
                        <TableHead>
                          {t("gavion.automation.columns.last_run")}
                        </TableHead>
                        <TableHead>
                          {t("gavion.automation.columns.runs")}
                        </TableHead>
                        <TableHead>
                          {t("gavion.automation.columns.failures")}
                        </TableHead>
                        <TableHead>
                          {t("gavion.automation.columns.enabled")}
                        </TableHead>
                        <TableHead>
                          {t("gavion.automation.columns.order")}
                        </TableHead>
                      </>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rules.map((r, i) => (
                    <TableRow key={r.id}>
                      <TableCell className="whitespace-normal">
                        <button
                          type="button"
                          className="text-left underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                          onClick={() => setEditing(r)}
                        >
                          {r.name}
                        </button>
                      </TableCell>
                      <TableCell>
                        {t(`gavion.automation.triggers.${r.trigger}`)}
                      </TableCell>
                      {admin && (
                        <>
                          <TableCell>{lastRun(r.id)}</TableCell>
                          <TableCell>{stats[r.id]?.runs7d ?? 0}</TableCell>
                          <TableCell>{stats[r.id]?.failures7d ?? 0}</TableCell>
                          <TableCell>
                            <Switch
                              checked={r.enabled}
                              aria-label={t("gavion.automation.enable", {
                                name: r.name,
                              })}
                              onCheckedChange={(enabled) =>
                                update(
                                  "automation_rules",
                                  {
                                    id: r.id,
                                    data: { enabled },
                                    previousData: r,
                                  },
                                  { mutationMode: "pessimistic" },
                                )
                              }
                            />
                          </TableCell>
                          <TableCell className="flex items-center gap-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              aria-label={t("gavion.automation.move_up", {
                                name: r.name,
                              })}
                              disabled={moving || i === 0}
                              onClick={() => void move(i, i - 1)}
                            >
                              <ArrowUp className="size-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              aria-label={t("gavion.automation.move_down", {
                                name: r.name,
                              })}
                              disabled={moving || i === rules.length - 1}
                              onClick={() => void move(i, i + 1)}
                            >
                              <ArrowDown className="size-4" />
                            </Button>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
        {admin && (
          <TabsContent value="history">
            <RunsHistory rules={rules} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};
